// Centralized mappings for the entire application
// This file contains ALL mappings used across the codebase to avoid duplication

// ============================================================================
// WEIGHTED AVERAGE CALCULATIONS
// ============================================================================
// Weight matrix for calculating weighted averages based on number of comps
export const WEIGHT_MATRIX = {
  1:  [1.0],
  2:  [0.5, 0.5],
  3:  [0.5, 0.3, 0.2],
  4:  [0.4, 0.3, 0.2, 0.1],
  5:  [0.35, 0.25, 0.20, 0.12, 0.08],
  6:  [0.30, 0.24, 0.18, 0.12, 0.09, 0.07],
  7:  [0.28, 0.22, 0.17, 0.12, 0.09, 0.07, 0.05],
  8:  [0.25, 0.20, 0.15, 0.12, 0.10, 0.08, 0.06, 0.04],
  9:  [0.22, 0.18, 0.14, 0.11, 0.10, 0.08, 0.07, 0.06, 0.04],
  10: [0.20, 0.17, 0.14, 0.12, 0.10, 0.08, 0.07, 0.06, 0.04, 0.02],
  11: [0.195, 0.162, 0.134, 0.112, 0.093, 0.077, 0.064, 0.053, 0.044, 0.036, 0.030],
  12: [0.190, 0.158, 0.131, 0.109, 0.090, 0.075, 0.062, 0.052, 0.043, 0.036, 0.030, 0.024],
  13: [0.187, 0.155, 0.129, 0.107, 0.089, 0.073, 0.061, 0.051, 0.042, 0.035, 0.029, 0.024, 0.018],
  14: [0.184, 0.152, 0.126, 0.105, 0.087, 0.072, 0.060, 0.050, 0.041, 0.034, 0.028, 0.024, 0.020, 0.017],
  15: [0.181, 0.150, 0.125, 0.104, 0.086, 0.071, 0.059, 0.049, 0.041, 0.034, 0.028, 0.023, 0.019, 0.016, 0.014],
  16: [0.179, 0.149, 0.123, 0.102, 0.085, 0.071, 0.059, 0.049, 0.040, 0.033, 0.028, 0.023, 0.019, 0.016, 0.013, 0.011],
  17: [0.177, 0.147, 0.122, 0.101, 0.084, 0.070, 0.058, 0.048, 0.040, 0.033, 0.028, 0.023, 0.019, 0.016, 0.013, 0.011, 0.010],
  18: [0.176, 0.146, 0.121, 0.101, 0.084, 0.069, 0.058, 0.048, 0.040, 0.033, 0.027, 0.023, 0.019, 0.016, 0.013, 0.011, 0.009, 0.006],
  19: [0.175, 0.145, 0.121, 0.100, 0.083, 0.069, 0.057, 0.048, 0.039, 0.033, 0.027, 0.023, 0.019, 0.016, 0.013, 0.011, 0.009, 0.007, 0.005],
  20: [0.174, 0.145, 0.120, 0.100, 0.083, 0.069, 0.057, 0.047, 0.039, 0.033, 0.027, 0.022, 0.019, 0.015, 0.013, 0.011, 0.009, 0.007, 0.006, 0.004]
};

/**
 * Unified weighted average calculation function -- we need to 
 * This ensures consistency between toolbar and summary table calculations
 * 
 * @param {Array} prices - Array of prices to calculate weighted average for
 * @returns {number} - Weighted average of the prices
 */
export const calculateWeightedAverage = (prices) => {
  if (!prices || prices.length === 0) return 0;
  
  const numComps = prices.length;
  const weights = WEIGHT_MATRIX[Math.min(numComps, 10)]; // Cap at 10 for our matrix
  
  if (!weights) {
    // Fallback to simple average if no weights defined
    return prices.reduce((acc, price) => acc + price, 0) / prices.length;
  }
  
  // Calculate weighted average
  let weightedSum = 0;
  
  prices.forEach((price, index) => {
    const weight = weights[index] || 0; // weights are 0-indexed arrays
    weightedSum += price * weight;
  });
  
  return weightedSum;
};

// ============================================================================
// WORKUP / REPORT TYPES (download PDF, send workups, regenerate)
// ============================================================================

/**
 * Negotiation report type -> { baseSuffix (S3 filename base), displayLabel (for generated filenames) }.
 * Single source for both file and display; village/loa handled here.
 */
export const NEGOTIATION_REPORT_TYPES = {
  standard: { baseSuffix: 'sales_comps', displayLabel: 'Sales' },
  assessment: { baseSuffix: 'equity_comps', displayLabel: 'Assessment' },
  village: { baseSuffix: 'village_comps', displayLabel: 'Village' },
  loa: { baseSuffix: 'loa_report', displayLabel: 'LOA' },
};

const _defaultNegotiation = NEGOTIATION_REPORT_TYPES.standard;

/** Single source of truth for workup report types. Add a new entry here (id, label, fileSuffix, optional internalFileName) to add a report type everywhere. */
export const WORKUP_REPORT_TYPES = [
  { id: 'fnmaSummary', label: 'FNMA', fileSuffix: '_fnma' },
  { id: 'onepagermaps', label: 'One Pager', fileSuffix: '' },
  // Generated via Include Maps toggle (not a standalone UI card); S3 name is e.g. sales_comps_nomaps.pdf
  { id: 'onepager', label: 'One Pager (No Maps)', fileSuffix: '_nomaps', hideFromUi: true },
  { id: 'loa', label: 'LOA Report', fileSuffix: '', internalFileName: 'loa_report' },
];

/**
 * Evidence type -> regenerateWorkups options (reportTypes + reportType). Label used for UI (NyscefUploadModal).
 * reportTypes use ids from WORKUP_REPORT_TYPES.
 */
export const EVIDENCE_TYPE_TO_REGEN_OPTIONS = {
  Unequal: { reportTypes: ['fnmaSummary', 'onepagermaps'], reportType: 'standard', label: 'Sales Analysis' },
  Excessive: { reportTypes: ['onepagermaps'], reportType: 'assessment', label: 'Assessment Analysis' },
};

export const getNegotiationReportTypeDisplayLabel = (reportType) =>
  NEGOTIATION_REPORT_TYPES[reportType]?.displayLabel ?? _defaultNegotiation.displayLabel;

export const getWorkupReportLabel = (id) =>
  WORKUP_REPORT_TYPES.find((r) => r.id === id)?.label ?? id;

export const getWorkupReportTypeIds = () => WORKUP_REPORT_TYPES.map((r) => r.id);

export const isWorkupPdfOption = (option) => getWorkupReportTypeIds().includes(option);

/** Ordered list of { value, label } for evidence-type selection UI. */
export const EVIDENCE_TYPES_FOR_SELECTION = Object.entries(EVIDENCE_TYPE_TO_REGEN_OPTIONS).map(([value, opts]) => ({ value, label: opts.label }));

/**
 * Returns the S3 filename (no extension) for a workup PDF, e.g. 'sales_comps_fnma', 'village_comps', 'loa_report'.
 * Uses internalFileName when set on the workup type (e.g. LOA); otherwise base + fileSuffix from negotiation/workup types.
 * @param {string} reportType - Negotiation report type: 'standard' | 'assessment' | 'village' | 'loa'
 * @param {string} typeOutput - Workup report type id (e.g. 'fnmaSummary', 'onepagermaps', 'loa')
 */
export const getWorkupS3FileName = (reportType, typeOutput) => {
  const workup = WORKUP_REPORT_TYPES.find((r) => r.id === typeOutput);

  // If you have an explicitly defined internal file name, use it.
  if (workup?.internalFileName) return workup.internalFileName;
  const base = NEGOTIATION_REPORT_TYPES[reportType]?.baseSuffix ?? _defaultNegotiation.baseSuffix;
  const fragment = workup?.fileSuffix ?? '';
  return base + fragment;
};

// ============================================================================
// PROPERTY STYLE OPTIONS
// ============================================================================
export const styleOptions = [
  'Colonial',
  'Ranch',
  'Cape Cod',
  'Contemporary',
  'Old Style',
  'Raised Ranch',
  'Split Level',
  'Town House',
  'Tudor',
  'Victorian',
  'Duplex',
  'Garden',
  'Condo',
  // 'Mobile Home',
];

// THESE ARE THE STYLE TYPES THAT NEED REMAPPING
export const SECONDARY_STYLE_MAPPINGS = {
  'Tudor': 'Colonial',
  'Victorian': 'Colonial',
  'Garden': 'Town House',
  'Duplex': 'Other',
};

// ============================================================================
// PROPERTY CONDITION & GRADE MAPPINGS
// ============================================================================
// View options
export const viewOptions = [
  'Waterfront', 'Waterview', 'None'
];

// Condition options - sorted by numeric value descending (Excellent first)
export const conditionOptions = [
  'Excellent', 'Good', 'Average', 'Fair', 'Poor'
];

// Grade mapping for data normalization
export const gradeMap = {
  'A': 'Excellent',
  'A+': 'Excellent',
  'A-': 'Excellent',
  'Average': 'Average',
  'B': 'Good',
  'B+': 'Good',
  'B-': 'Good',
  'C': 'Average',
  'C+': 'Average',
  'C-': 'Average',
  'D': 'Fair',
  'D+': 'Fair',
  'D-': 'Fair',
  'E': 'Poor',
  'Economy': 'Fair',
  'Excellent': 'Excellent',
  'Good': 'Good',
  'Poor': 'Poor',
  'Fair': 'Fair',
  'Minimum': 'Poor',
  'S': 'Average',
  'S+': 'Average',
  'S-': 'Average',
  'X': 'Average',
  'X+': 'Average',
  'X-': 'Average',
  'Z': 'Average',
  'Unknown': 'Average',
};

// Condition mapping for data normalization
export const conditionMap = {
  'Excellent': 'Excellent',
  'Very Good': 'Good',
  'Good': 'Good',
  'Normal': 'Average',
  'Average': 'Average',
  'Fair': 'Fair',
  'Poor': 'Poor',
  'Very Poor': 'Poor',
};

// Condition to numeric mapping for calculations
export const conditionNumericMap = {
  'Excellent': 5,
  'Very Good': 4,
  'Good': 4,
  'Normal': 3,
  'Average': 3,
  'Fair': 2,
  'Poor': 1,
  'Very Poor': 1,
  'Unknown': 3,
};

// Grade to numeric mapping for calculations
export const gradeNumericMap = {
  'A+': 5,
  'A': 5,
  'A-': 5,
  'Average': 3,
  'Unknown': 3,
  'B+': 4,
  'B': 4,
  'B-': 4,
  'C+': 3,
  'C': 3,
  'C-': 3,
  'D+': 2,
  'D': 2,
  'D-': 2,
  'E': 1,
  'Economy': 2,
  'Excellent': 5,
  'Good': 4,
  'Poor': 1,
  'Fair': 2,
  'Minimum': 1,
  'S': 3,
  'S+': 3,
  'S-': 3,
  'X': 3,
  'X+': 3,
  'X-': 3,
  'Z': 3,
  'F': 1,
};

// Grade options - consolidated from gradeMap values, sorted by numeric value descending (Excellent first)
export const gradeOptions = [...new Set(Object.values(gradeMap))].sort((a, b) => {
  return gradeNumericMap[b] - gradeNumericMap[a];
});

// ============================================================================
// FIELD MAPPINGS
// ============================================================================

// Expected backend keys (excluding pid which is handled separately)
export const AVTN_BACKEND_UPDATE_KEYS = [
  'BuildingStyle', 'BathsFull', 'BathsHalf', 'Bedrooms', 'Kitchens', 
  'Fireplaces', 'ConditionIn', 'Grade', 'PorchArea', 'YearBuilt', 
  'EffYearBuilt', 'BasementType', 'GarageCapacity', 'LivingArea', 
  'FinishedBasement', 'CentralAir', 'InGroundPool', 'Garage', 
  'Porch', 'Patio', 'Deck', 'LotSquareFeet', 'Acres', 
  'Inventory_Price', 'DateUpdated_Property_Inventory', 
  'BuildingStyleGroup', 'NeighborhoodCode', 'TotalRooms', 
  'TennisCourt', 'NumStories', 'UserID', 'SecondaryStyle', 
  'Assessment', 'Year', 'Waterfront', 'Waterview', 
  'RegridBldgFtPrnt', 'RegridConvertedSqFt', 'Latitude', 'Longitude'
];
// Database to UI field mappings, Db is on the LEFT (this is what comes back from the backend)
export const fieldMappings = {
  'WaterView': 'Waterview',
  'ParcelID': 'parcel_id',
  'LivingArea': 'Sqft',
  'PropertyAssessment': 'PropertyAssessment',
  'PropertyClass': 'PCC',
  'BuildingStyle': 'Style',
  'YearBuilt': 'YearBuilt',
  'EffYearBuilt': 'EffYearBuilt',
  'Acres': 'Acres',
  'Bath': 'fullBaths',
  'HalfBath': 'halfBaths',
  // Additional mappings for API field names
  'BathsFull': 'fullBaths',
  'BathsHalf': 'halfBaths',
  'totalBaths': 'totalBaths',
  'IFMVTown': 'IFMV',
  'SalePrice': 'SalePrice',
  'SaleDate': 'SaleDate',
  'MLSNumber': 'MLSNumber',
  'InGroundPool': 'Pool',
  'ConditionIn': 'Condition',
  'Grade': 'Grade',
  'Distance': 'Distance',
  'Deck': 'Deck',
  'Porch': 'Porch',
  'Heating': 'Heating',
  'Fireplaces': 'Fireplaces',
  'Kitchens': 'Kitchens',
  'Patio': 'Patio',
  'FinishedBasement': 'FinishedBasement',
  'CentralAir': 'CentralAir',
  'ContractDate': 'ContractDate',
  'TotalRooms': 'TotalRooms',
  'Remarks': 'Remarks',
  'Address': 'Address',
  'Town': 'Town',
  'Zip5': 'ZipCode',
  'NeighborhoodCode': 'NeighborhoodCode',
  'SDName': 'SDName',
  'SDCode': 'SDCode',
  'LastDataUpdate': 'LastDataUpdate',
  'PreviousAssessment': 'PrevAssessment',
  
  // Additional fields from headers
  'TimeAdjustment': 'time_adj',
  'AdjustedPrice': 'adj_price',
  'AdjustedIFMV': 'adj_price_IFMV',
  'RegridConvertedSquareFeet': 'RegridConvertedSqFt',
  'EstimatedSquareFeet': 'EstSqft',
  'TotalGarageCapacity': 'TotalGarageCap',
  'GarageCapacity': 'TotalGarageCap', // Additional mapping for API field name
  'Waterfront': 'Waterfront', // needed for View calculation
  'RegridBldgFtPrnt': 'RegridBldgFtPrnt',
};

// Column header mappings for human-readable display
export const columnHeaderMappings = {
  'Bath': 'Full Baths',
  'HalfBath': 'Half Baths',
  'Sqft': 'Square Feet',
  'Acres': 'Acreage',
  'YearBuilt': 'Year Built',
  'EffYearBuilt': 'Eff Year Built',
  'SalePrice': 'Sale Price',
  'SaleDate': 'Sale Date',
  'TransactionID': 'Sale Transaction ID',
  'AssessedValue': 'Assessed Value',
  'MarketValue': 'Market Value',
  'PhotoURL': 'Photo URL',
  'ParcelId': 'Parcel ID',
  'ZipCode': 'Zip Code',
  'Style': 'Style',
  'Latitude': 'Latitude',
  'Longitude': 'Longitude',
  'InGroundPool': 'Pool',
  'TotalGarageCap': 'Garage Capacity',
  'IsSalesComp': 'Sales Comp',
  'IsAssessmentComp': 'Equity Comp',
  'IFMV': 'IFMV',
  'IFMVRatio': 'IFMV Ratio',
  'Taxes': 'Taxes',
  'Town': 'Town',
  'Address': 'Address',
  'PCC': 'PCC',
  'parcel_id': 'Parcel ID',
  'Client': 'Client',
  'Description': 'Description',
  'ALFlag': 'Arms Length',
  'FinishedBasement': 'Finished Basement',
  'MLSNumber': 'MLS Number',
  'LastDataUpdate': 'Inv. Update',
  'PrevAssessment': 'Prev. Assessment',
  'ConditionIn': 'Condition',
  'Grade': 'Grade',
  'Deck': 'Deck',
  'Porch': 'Porch',
  'Patio': 'Patio',
  'Fireplaces': 'Fireplaces',
};

export const excludedFields = ['SecondaryStyle', 'InGroundPool'];

// ============================================================================
// TASK & CASE MANAGEMENT OPTIONS
// ============================================================================
// Stage options for task management
export const stageOptions = [
  { value: 'all', label: 'All Stages' },
  { value: 'BAR', label: 'BAR' },
  { value: 'SCAR', label: 'SCAR' },
  { value: 'incomplete', label: 'Incomplete' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'completed', label: 'Completed' },
];

// Status options for case management
export const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'Unsettled', label: 'Active' },
  { value: 'Settled', label: 'Settled' },
];

// Review type options
export const reviewTypeOptions = [
  { value: 'all', label: 'All Types' },
  { value: 'DataCleaning', label: 'Data Review' },
  { value: 'CaseReview', label: 'Case Review' },
  { value: 'Negotiation', label: 'Negotiation' },
  { value: 'Settled', label: 'Settled' },
];

// Town view specific stage options
export const townViewStageOptions = [
  { value: 'all', label: 'All Cases' },
  { value: 'BAR', label: 'BAR Cases' },
  { value: 'SCAR', label: 'SCAR Cases' },
];

// ============================================================================
// GEOGRAPHIC MAPPINGS
// ============================================================================
// Default state for address formatting
export const STATE = 'NY';
// County codes mapping
export const COUNTY_CODES = {
  C: 'Columbia',
  D: 'Dutchess',
  G: 'Greene',
  L: 'Delaware',
  N: 'Nassau',
  O: 'Orange',
  P: 'Putnam',
  R: 'Rockland',
  S: 'Suffolk',
  U: 'Ulster',
  V: 'Sullivan',
  W: 'Westchester',
};

// ============================================================================
// DATA GRID & TABLE CONFIGURATIONS
// ============================================================================
// Default fields to check for bad data
export const DEFAULT_BAD_DATA_FIELDS = [
  'Style',
  'Sqft',
  'Acres',
  'YearBuilt',
  'Bath',
  'HalfBath',
  'Address',
  'PropertyAssessment',
  'IFMV',
  'Taxes',
  'PhotoURL',
  'Latitude',
  'Longitude',
  'TotalGarageCap'
];

// County-specific bad data parameters 0 only customized the muni level ones for seveal in suffolk county.
export const BAD_DATA_PARAMS = {
  // Suffolk County (S) - exclude Bath field as fallback (This is stupid now that we have all these specific ones)
  'S': {
    data: ['Style', 'Sqft', 'Acres', 'YearBuilt', 'Address', 'Bath', 'PropertyAssessment', 'IFMV', 'PhotoURL', 'Latitude', 'Longitude'],
    outliers: []
  },
  'S01': {
    data: ['Style', 'Sqft', 'Acres', 'YearBuilt', 'Address', 'Bath', 'PropertyAssessment', 'IFMV', 'PhotoURL', 'Latitude', 'Longitude', 'TotalGarageCap'],
    outliers: []
  },
  'S04': {
    data: ['Style', 'Sqft', 'Acres', 'YearBuilt', 'Address', 'PropertyAssessment', 'IFMV', 'PhotoURL', 'Latitude', 'Longitude', 'TotalGarageCap'],
    outliers: []
  },
  'S05': {
    data: ['Style', 'Sqft', 'Acres', 'YearBuilt', 'Address', 'Bath', 'PropertyAssessment', 'IFMV', 'PhotoURL', 'Latitude', 'Longitude', 'TotalGarageCap'],
    outliers: []
  },
  // Default for all other counties
  'default': {
    data: ['Style', 'Sqft', 'Acres', 'YearBuilt', 'Bath', 'Address', 'PropertyAssessment', 'IFMV', 'PhotoURL', 'Latitude', 'Longitude', 'TotalGarageCap'],
    outliers: []
  }
};

// Helper function to get bad data params for a specific muni code
export const getBadDataParams = (muniCode) => {
  if (!muniCode) return BAD_DATA_PARAMS.default;
  
  // First check for specific municipality code (e.g., S02)
  if (BAD_DATA_PARAMS[muniCode]) {
    return BAD_DATA_PARAMS[muniCode];
  }
  
  // Then check for county code (e.g., S)
  const countyCode = muniCode.charAt(0);
  return BAD_DATA_PARAMS[countyCode] || BAD_DATA_PARAMS.default;
};

// All available fields that can be checked for bad data
export const AVAILABLE_BAD_DATA_FIELDS = [
  ...DEFAULT_BAD_DATA_FIELDS,
  'Town',
  'ZipCode'
];

// Fields that are numeric and editable
export const numericEditableFields = [
  'Sqft', 
  'Bath', 
  'YearBuilt', 
  'HalfBath', 
  'SalePrice', 
  'Taxes', 
  'IFMV', 
  'IFMVRatio',
  'Acres', 
  'TotalGarageCap'
];

// Fields that are boolean-like
export const booleanLikeFields = [
  'Client', 
  'IsSalesComp', 
  'IsAssessmentComp', 
  'InGroundPool',
  'ALFlag'
];

// Fields that are always editable regardless of validation
export const ALWAYS_EDITABLE_FIELDS = [
  'Style', 
  'Bath', 
  'HalfBath', 
  'Bedrooms', 
  'Kitchens', 
  'Fireplaces', 
  'ConditionIn', 
  'Grade', 
  'PorchArea', 
  'YearBuilt', 
  'EffYearBuilt', 
  'BasementType', 
  'TotalGarageCap', 
  'Sqft', 
  'FinishedBasement', 
  'CentralAir', 
  'InGroundPool', 
  'Garage', 
  'Porch', 
  'Patio', 
  'Deck', 
  'LotSquareFeet', 
  'Acres', 
  'Inventory_Price', 
  'DateUpdated_Property_Inventory', 
  'BuildingStyleGroup', 
  'NeighborhoodCode', 
  'TotalRooms', 
  'TennisCourt', 
  'NumStories', 
  'UserID', 
  'SecondaryStyle', 
  'Assessment', 
  'Year', 
  'Waterfront', 
  'Waterview', 
  'RegridBldgFtPrnt', 
  'RegridConvertedSqFt', 
  'Latitude', 
  'Longitude'
];

// Default columns displayed in the data grid (order determines column order)
export const DEFAULT_COLUMNS_DISPLAYED = [
  'parcel_id',
  'Address',
  'Style',
  'Sqft',
  'YearBuilt',
  'Bath',
  'HalfBath',
  'Acres',
  'PhotoURL',
  'Latitude',
  'Longitude',
  'Town',
  'ZipCode',
  'InGroundPool',
  'TotalGarageCap',
  'FinishedBasement',
  'SalePrice',
  'SaleDate',
  'MLSNumber',
  'IFMV',
  'IFMVRatio',
  'ALFlag',
  'Taxes',
  'PropertyAssessment',
  'MarketValue',
  'AssessedValue',
  'PCC',
];

// Fields used for outlier detection
export const OUTLIER_FIELDS = [
  'Sqft',
  'Acres',
  'YearBuilt',
  'Bath',
  'SalePrice',
  'Style',
  'TotalGarageCap'
];


// Fields that are editable in the data grid
export const EDITABLE_FIELDS = [
  'Sqft', 
  'Bath', 
  'YearBuilt', 
  'HalfBath', 
  'SalePrice', 
  'Taxes', 
  'IFMV', 
  'Acres', 
  'Style', 
  'InGroundPool', 
  'TotalGarageCap'
];

// Field type definitions for data grid columns
export const FIELD_TYPES = {
  // Numeric fields
  'Client': 'number',
  'IsSalesComp': 'number',
  'IsAssessmentComp': 'number',
  'Sqft': 'number',
  'Acres': 'number',
  'YearBuilt': 'number',
  'EffYearBuilt': 'number',
  'Bath': 'number',
  'HalfBath': 'number',
  'TotalGarageCap': 'number',
  'FinishedBasement': 'number',
  'Latitude': 'number',
  'Longitude': 'number',
  'PropertyAssessment': 'number',
  'MarketValue': 'number',
  'AssessedValue': 'number',
  'IFMVRatio': 'number',
  
  // Currency fields
  'SalePrice': 'currency',
  'Taxes': 'currency',
  'IFMV': 'currency',
  'MarketValue': 'currency',
  'AssessedValue': 'currency',
  
  // Date fields
  'SaleDate': 'date',
  
  // Boolean fields
  'InGroundPool': 'boolean',
  'Client': 'boolean',
  'IsSalesComp': 'boolean',
  'IsAssessmentComp': 'boolean',
  'ALFlag': 'number'
};

// ============================================================================
// CASE REVIEW GRID FIELD CONFIG
// ============================================================================
// Source of truth for Case Review column order, row keys, and simple columns.
// Constructors iterate this list. A custom column def is optional.
// - field: DataGrid field and row key
// - dbField: comps/subject source key (null when the value is computed elsewhere)
// - headerName: set this to auto-build a plain column (no custom def)
// - hidden: default column visibility off
// - summary: also show on the summary case table
export const CASE_REVIEW_COLUMNS = [
  { field: 'Address', dbField: 'Address' },
  { field: 'PhotoURL', dbField: 'PhotoURL' },
  { field: 'Town', dbField: 'Town' },
  { field: 'SDName', dbField: 'SDName' },
  { field: 'SDCode', dbField: 'SDCode' },
  { field: 'ZipCode', dbField: 'ZipCode' },
  { field: 'NeighborhoodCode', dbField: 'NeighborhoodCode' },
  { field: 'parcel_id', dbField: 'parcel_id' },
  { field: 'TransactionID', dbField: 'TransactionID', headerName: 'Sale Transaction ID', hidden: true, summary: true },
  { field: 'PCC', dbField: 'PCC' },
  { field: 'Style', dbField: 'Style' },
  { field: 'View', dbField: null }, // derived from Waterfront / Waterview
  { field: 'YearBuilt', dbField: 'YearBuilt' },
  { field: 'EffYearBuilt', dbField: 'EffYearBuilt' },
  { field: 'Sqft', dbField: 'Sqft' },
  { field: 'EstSqft', dbField: 'Sqft_pred' },
  { field: 'RegridBldgFtPrnt', dbField: 'RegridBldgFtPrnt' },
  // { field: 'RegridConvertedSqFt', dbField: 'RegridConvertedSqFt' },
  { field: 'Acres', dbField: 'Acres' },
  { field: 'Grade', dbField: 'Grade' },
  { field: 'Condition', dbField: 'ConditionIn' },
  { field: 'Pool', dbField: 'InGroundPool' },
  { field: 'FinishedBasement', dbField: 'FinishedBasement' },
  { field: 'TotalGarageCap', dbField: 'TotalGarageCap' },
  { field: 'Fireplaces', dbField: 'Fireplaces' },
  { field: 'Deck', dbField: 'Deck' },
  { field: 'Patio', dbField: 'Patio' },
  { field: 'Porch', dbField: 'Porch' },
  { field: 'fullBaths', dbField: 'Bath' },
  { field: 'halfBaths', dbField: 'HalfBath' },
  { field: 'totalBaths', dbField: 'totalBaths' },
  { field: 'Kitchens', dbField: 'Kitchens' },
  { field: 'Bedrooms', dbField: 'Bedrooms' },
  { field: 'CentralAir', dbField: 'CentralAir' },
  { field: 'Taxes', dbField: 'Taxes' },
  { field: 'PropertyAssessment', dbField: 'PropertyAssessment' },
  { field: 'PrevAssessment', dbField: 'PreviousAssessment' },
  { field: 'IFMV', dbField: 'IFMV' },
  { field: 'adj_IFMV', dbField: 'adj_IFMV' },
  { field: 'be_adj_IFMV', dbField: null }, // backend adj_IFMV, same role as be_adj_price
  { field: 'SalePrice', dbField: 'SalePrice' },
  { field: 'LOA', dbField: null }, // computed from SalePrice / Assessment
  { field: 'CalculatedTAV', dbField: null }, // sale price × RAR year from sale date vs eval date
  { field: 'SaleDate', dbField: 'SaleDate' },
  { field: 'LastDataUpdate', dbField: 'LastDataUpdate' },
  { field: 'ContractDate', dbField: 'ContractDate' },
  { field: 'MLSNumber', dbField: 'MLSNumber' },
  { field: 'ALFlag', dbField: 'ALFlag' },
  { field: 'adj_price', dbField: 'adj_price' },
  { field: 'be_adj_price', dbField: null }, // backend / computed
  { field: 'gross_adj_pct', dbField: null }, // computed from adjustment breakdown
  { field: 'net_adj_pct', dbField: null }, // computed from adjustment breakdown
  { field: 'time_adj', dbField: 'TimeAdjSalePrice' },
  { field: 'Distance', dbField: 'Distance' },
];

// ============================================================================
// VISUALIZATION & CHART OPTIONS
// ============================================================================
// Chart type options
export const chartOptions = [
  { label: "Distribution", icon: "CandlestickChartIcon", value: "Distribution" },
  { label: "X,Y Scatter", icon: "ScatterPlotIcon", value: "X,Y Scatter" },
  { label: "3-D Linear", icon: "ShowChartIcon", value: "3-D Scatter", linear: true },
  { label: "3-D Propriety", icon: "AutoGraphIcon", value: "3-D Scatter" },
];


// ============================================================================
// BACKEND TO FRONTEND FIELD MAPPINGS
// ============================================================================
// Mapping from Python backend adjustment keys to frontend comps object keys
export const PythonBackendToFrontend = {
  // Direct mappings (same name)
  'SalePrice': 'SalePrice',
  'IFMV': 'IFMV',
  'Acres': 'Acres',
  'Bath': 'Bath',
  'HalfBath': 'HalfBath',
  'Sqft': 'Sqft',
  'YearBuilt': 'YearBuilt',
  'EffYearBuilt': 'EffYearBuilt',
  'TotalGarageCap': 'TotalGarageCap',
  'CentralAir': 'CentralAir',
  'InGroundPool': 'InGroundPool',
  'Fireplaces': 'Fireplaces',
  'Kitchens': 'Kitchens',
  'Deck': 'Deck',
  'Patio': 'Patio',
  'Porch': 'Porch',
  'FinishedBasement': 'FinishedBasement',
  'ConditionIn': 'ConditionIn',
  'Grade': 'Grade',
  'Waterfront': 'Waterfront',
  'Waterview': 'Waterview',
  'Style': 'Style',
  'Bedrooms': 'Bedrooms',
  'Heating': 'Heating',
  'TotalRooms': 'TotalRooms',
  'NeighborhoodCode': 'NeighborhoodCode',
  'PropertyAssessment': 'PropertyAssessment',
  'Taxes': 'Taxes',
  'Latitude': 'Latitude',
  'Longitude': 'Longitude',
  'Town': 'Town',
  'ZipCode': 'ZipCode',
  'SaleDate': 'SaleDate',
  'MLSNumber': 'MLSNumber',
  'ContractDate': 'ContractDate',
  'Remarks': 'Remarks',
  'PhotoURL': 'PhotoURL',
  'Distance': 'Distance',
  'PCC': 'PCC',
  'SDName': 'SDName',
  'SDCode': 'SDCode',
  'Address': 'Address',
  'RegridBldgFtPrnt': 'RegridBldgFtPrnt',
  'RegridConvertedSqFt': 'RegridConvertedSqFt',
  'ALFlag': 'ALFlag',
  'TimeAdjSalePrice': 'TimeAdjSalePrice',
  'SecondaryStyle': 'SecondaryStyle',
  'MuniCode': 'MuniCode',
  'MuniCode5': 'MuniCode5',
  'AssessmentVillage': 'AssessmentVillage',
  'IFMV_Village': 'IFMV_Village',
  'adj_price': 'adj_price',
  'adj_price_IFMV': 'adj_price_IFMV',
  'Sqft_pred': 'Sqft_pred',
  'SalePrice_pred': 'SalePrice_pred',
  'price_delta': 'price_delta',
  'value_delta': 'value_delta',
  'WorkupValueAvg': 'WorkupValueAvg',
  'WorkupValueMid': 'WorkupValueMid',
  
  // Backend-specific fields that don't have direct frontend equivalents
  'BasementSqFtUnfinished': null, // Not in frontend comps
  'Bulkhead': null,
  'County': null,
  'Crawl': null,
  'District': null,
  'Full': null,
  'FullFinished': null,
  'FullPartialFinish': null,
  'FullUnfinished': null,
  'GarageDet': null,
  'LotSqFt': null,
  'Partial': null,
  'PoolAg': null,
  'PoolUnsure': null,
  'countycode': null,
  'distanceradius': null,
  'muniCode': null,
  'taxYear': null,
  'manuallyAddedComps': null,
  'specificPid': null,
  'target_id': null,
  'StreetName': null,
  'StreetSuffix': null,
  'EQ': null,
  'RAR': null,
  'Client': null,
  'RepID': null,
  'ClientName': null,
  'OwnerFirstName': null,
  'OwnerLastName': null,
  'OfferValue': null,
  'PortfolioOffer': null,
  'SCARDeterminationAction': null,
  'SCARDeterminationValue': null,
  'SCARSettleDate': null,
  'BARDeterminationAction': null,
  'BARDeterminationValue': null,
  'SCARIndexNumber': null,
  'BARFiled': null,
  'SCARFiled': null,
  'VillageSCARFiled': null,
  'BARRequestedValue': null,
  'Comps': null,
  'RunDate': null,
  'SubjectMarketValue': null,
  'CaseNotes': null,
  'MiscAdjustments': null,
  'NassauPID': null,
  'do_not_contact': null,
  'prev_reduction': null,
  'do_not_mail': null,
  'PhotoSource': null,
  'subject': null,
  'index_position': null,
  'price_adjustments': null
};

// ============================================================================
// SPECIAL FORMATTERS & COMPOSITE FIELDS
// ============================================================================
// Special composite field mapping
export const compositeFields = {
  'PropertyInfo': {
    fields: ['Address', 'parcel_id', 'SDName', 'SDCode'],
    formatter: (values) => ({
      Address: values.Address || '',
      parcel_id: values.parcel_id || '',
      SDName: values.SDName || '',
      SDCode: values.SDCode || ''
    })
  }
};

export const specialFormatters = {
  'Pool': (value) => value === 1 ? 1 : '',
  'View': (waterfront, waterview) => 
    waterfront === 1 ? 'Waterfront' : 
    waterview === 1 ? 'Waterview' : '',
  'totalBaths': (bath, halfBath) => 
    (bath || 0) + (halfBath || 0) / 2,
  'RegridBldgFtPrnt': (value) => 
    value ? Number(value) + 0.0001 : '',
  'PropertyInfo': (values) => ({
    Address: values.Address || '',
    parcel_id: values.parcel_id || '',
    SDName: values.SDName || '',
    SDCode: values.SDCode || ''
  })
};